import React, { useState } from 'react';
import { doc, getDoc, writeBatch, serverTimestamp, increment } from 'firebase/firestore';
import { db } from '../db';
import { PLANS, LAUNCH_PRICE, REGULAR_PRICE, LATPEED_URL, TRIAL_DAYS, getTrialEnd } from '../usePlan';

const PLAN_FEATURES = {
  free: [
    '모든 Pro 기능을 3일간 무료 체험',
    '가입 즉시 시작 — 카드 등록 불필요',
    '체험 중 입력한 데이터는 이용권 등록 후 그대로 이어서 사용',
  ],
  pro: [
    '거래장부·단가표·레시피·발주서·영수증 OCR 전부 포함',
    '거래장부 무제한 · 영수증 OCR 총 1,000장 (월 100장, 충전권으로 추가 가능)',
    '매장 손익 관리 (P&L)',
    '세무 도우미 풀세트 (부가세·종합소득세·의제매입공제)',
    'POS 매출 엑셀 일괄 임포트',
    '식자재 가격 추이 + 평균가 시뮬레이터',
    '재고 분석 + 적정 매입 분석',
    '메뉴 이익률 + 가격 결정 도우미',
    '직원 관리 + 급여 계산',
    '공동구매 참여',
    'AI 발주 추천',
    '공급처 마스터',
    '우선 고객 지원',
    '향후 업데이트 평생 무료',
  ],
};

const S = {
  wrap: {
    padding: '24px 20px 80px',
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    textAlign: 'center',
    marginBottom: '32px',
  },
  title: {
    fontSize: '1.5rem',
    fontWeight: '800',
    color: '#1a1a2e',
  },
  sub: {
    fontSize: '0.9rem',
    color: '#6b7280',
    marginTop: '6px',
  },
  graceBanner: {
    margin: '16px auto 0',
    maxWidth: '560px',
    background: '#FFF7ED',
    border: '1px solid #FDBA74',
    borderRadius: '12px',
    padding: '12px 16px',
    fontSize: '0.82rem',
    color: '#9A3412',
    lineHeight: 1.6,
    textAlign: 'left',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
    gap: '16px',
  },
  card: (active, color) => ({
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: active
      ? `0 0 0 3px ${color}, 0 8px 24px rgba(0,0,0,0.12)`
      : '0 2px 12px rgba(0,0,0,0.08)',
    transition: 'all 0.2s',
    cursor: 'pointer',
  }),
  cardHead: (color) => ({
    background: color,
    padding: '20px 18px 16px',
    color: '#fff',
  }),
  planName: {
    fontSize: '1rem',
    fontWeight: '800',
    marginBottom: '4px',
  },
  planPrice: {
    fontSize: '1.8rem',
    fontWeight: '900',
    lineHeight: 1,
  },
  planPriceUnit: {
    fontSize: '0.8rem',
    fontWeight: '400',
    opacity: 0.85,
    marginLeft: '4px',
  },
  planPriceNote: {
    fontSize: '0.75rem',
    fontWeight: '600',
    opacity: 0.9,
    marginTop: '6px',
  },
  cardBody: {
    padding: '16px 18px 20px',
  },
  feature: {
    fontSize: '0.8rem',
    color: '#374151',
    padding: '5px 0',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '7px',
    borderBottom: '1px solid #f5f5f5',
    lineHeight: 1.5,
  },
  check: (color) => ({
    color: color,
    fontWeight: '800',
    flexShrink: 0,
    marginTop: '1px',
  }),
  btn: (color) => ({
    marginTop: '16px',
    width: '100%',
    padding: '11px',
    borderRadius: '10px',
    border: 'none',
    background: color,
    color: '#fff',
    fontWeight: '800',
    fontSize: '0.9rem',
    cursor: 'pointer',
  }),
  currentBadge: (color) => ({
    marginTop: '16px',
    width: '100%',
    padding: '11px',
    borderRadius: '10px',
    border: `2px solid ${color}`,
    background: '#fff',
    color: color,
    fontWeight: '800',
    fontSize: '0.9rem',
    textAlign: 'center',
  }),
  notice: {
    marginTop: '32px',
    background: '#f8fafc',
    borderRadius: '12px',
    padding: '16px 20px',
    fontSize: '0.78rem',
    color: '#6b7280',
    lineHeight: 1.7,
  },
};

const fmtDate = (d) =>
  `${d.getFullYear()}년 ${d.getMonth() + 1}월 ${d.getDate()}일`;

const fmtDateTime = (ms) => {
  const d = new Date(ms);
  return `${fmtDate(d)} ${d.getHours()}시`;
};

export default function SubscriptionPage({ user, currentPlan, isLifetime, onClose }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [codeInput, setCodeInput]   = useState('');
  const [codeState, setCodeState]   = useState(null); // null | 'loading' | 'success' | {error}

  // ── 이용권 코드 등록 (래피드 구매자) ──
  const redeemCode = async () => {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setCodeState('loading');
    try {
      const codeRef = doc(db, 'licenseCodes', code);
      const snap = await getDoc(codeRef);
      if (!snap.exists()) {
        setCodeState({ error: '유효하지 않은 코드예요. 오타가 없는지 확인해주세요.' });
        return;
      }
      if (snap.data().status !== 'unused') {
        setCodeState({ error: '이미 사용된 코드예요. 문제가 있다면 구매처로 문의해주세요.' });
        return;
      }
      const kind = snap.data().kind || 'lifetime';
      const batch = writeBatch(db);
      batch.update(codeRef, {
        status: 'redeemed',
        redeemedBy: user.uid,
        redeemedEmail: user.email || '',
        redeemedAt: serverTimestamp(),
      });
      if (kind === 'ocr100') {
        // V103: OCR 충전권 (100장) — ocrCredits에 적립
        batch.set(doc(db, 'users', user.uid), {
          ocrCredits: increment(100),
        }, { merge: true });
      } else {
        batch.set(doc(db, 'users', user.uid), {
          subscription: {
            plan: 'pro', status: 'active', lifetime: true,
            licenseCode: code, source: 'latpeed',
            startDate: serverTimestamp(),
          },
        }, { merge: true });
      }
      await batch.commit();
      setCodeState(kind === 'ocr100' ? 'credit_success' : 'success');
    } catch (e) {
      console.error(e);
      setCodeState({ error: '코드 등록 중 오류가 발생했어요. 잠시 후 다시 시도해주세요.' });
    }
  };

  const handlePurchase = (planKey) => {
    if (planKey === 'free') return;
    if (planKey === 'pro' && isLifetime) return;
    if (planKey === 'franchise') {
      window.open('mailto:?subject=프랜차이즈 플랜 문의', '_blank');
      return;
    }
    // V101: 래피드에서 이용권 구매 → 코드 전달 → 아래 코드 입력으로 활성화
    window.open(LATPEED_URL, '_blank', 'noopener');
  };

  const planList = [
    { key: 'free',      color: '#6b7280' },
    { key: 'pro',       color: '#EA580C' },
    { key: 'franchise', color: '#7C3AED' },
  ];

  // V102: 가입 후 3일 무료 체험 상태
  const trialEnd    = getTrialEnd(user);
  const trialActive = !!trialEnd && Date.now() < trialEnd;

  return (
    <div style={S.wrap}>
      <div style={S.header}>
        <div style={{ fontSize: '2rem', marginBottom: '8px' }}>💳</div>
        <div style={S.title}>평생 이용권</div>
        <div style={S.sub}>
          {isLifetime
            ? <>평생 Pro 이용 중이에요. 감사합니다! 🧡</>
            : <>한 번 결제하면 <strong>평생</strong> 모든 Pro 기능을 쓸 수 있어요. 월 이용료 없음.</>}
        </div>
        {!isLifetime && (
          <div style={S.graceBanner}>
            {trialActive ? (
              <>
                ⏰ <strong>무료 체험 중</strong> — {fmtDateTime(trialEnd)}까지 모든 기능을
                무료로 쓸 수 있어요 (가입 후 {TRIAL_DAYS}일). 체험이 끝나면 이용권 코드를
                등록해야 계속 쓸 수 있어요.
              </>
            ) : (
              <>
                ⏰ <strong>무료 체험 종료</strong> — 가입 후 {TRIAL_DAYS}일 체험이 끝났어요.
                입력하신 데이터는 그대로 보관 중이고, 이용권 코드를 등록하면 바로 이어서 쓸 수 있어요.
              </>
            )}
            {' '}지금은 런칭 특가 {LAUNCH_PRICE.toLocaleString()}원 (30명 한정) —
            이후 {REGULAR_PRICE.toLocaleString()}원으로 올라요.
          </div>
        )}
      </div>

      <div style={S.grid}>
        {planList.map(({ key, color }) => {
          const plan = PLANS[key];
          const features = PLAN_FEATURES[key] || [];
          const isCurrent =
            key === 'pro' ? isLifetime :
            key === 'free' ? (currentPlan === 'free') :
            key === currentPlan;

          return (
            <div key={key} style={S.card(isCurrent, color)}
                 onClick={() => !loading && handlePurchase(key)}>
              <div style={S.cardHead(color)}>
                <div style={S.planName}>{key === 'free' ? '3일 무료 체험' : plan.label}</div>
                <div style={S.planPrice}>
                  {key === 'franchise'
                    ? '문의'
                    : plan.price === 0
                      ? '무료'
                      : `${plan.price.toLocaleString()}원`}
                  {key === 'pro' && <span style={S.planPriceUnit}>1회 결제</span>}
                </div>
                {key === 'pro' && (
                  <div style={S.planPriceNote}>런칭 특가 · 30명 한정 (이후 {REGULAR_PRICE.toLocaleString()}원)<br/>월 이용료 없음 · 평생 소장</div>
                )}
              </div>
              <div style={S.cardBody}>
                {features.map((f, i) => (
                  <div key={i} style={S.feature}>
                    <span style={S.check(color)}>✓</span>
                    <span>{f}</span>
                  </div>
                ))}
                {isCurrent ? (
                  <div style={S.currentBadge(color)}>
                    {key === 'pro' ? '✓ 평생 소장 중'
                      : key === 'free' ? (trialActive ? '✓ 체험 중' : '체험 종료')
                      : '✓ 현재 플랜'}
                  </div>
                ) : (
                  <button style={S.btn(color)} disabled={loading}>
                    {key === 'free' ? '가입 시 자동 시작' :
                     key === 'franchise' ? '문의하기' : '래피드에서 평생 소장하기 →'}
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && (
        <div style={{ marginTop: '16px', color: '#52525B', fontSize: '0.85rem', textAlign: 'center' }}>
          {error}
        </div>
      )}

      {/* ── 이용권·충전권 코드 등록 (V100/V103 — 래피드 구매자) — lifetime 사용자도 충전권 등록 가능 ── */}
      {(
        <div style={{
          marginTop: '28px', background: '#fff', borderRadius: '16px',
          padding: '20px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
          maxWidth: '480px', marginLeft: 'auto', marginRight: 'auto',
        }}>
          <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: '4px' }}>
            🎟️ 이용권·충전권 코드가 있으신가요?
          </div>
          <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '12px' }}>
            래피드에서 구매하신 코드를 입력하면 바로 적용돼요. (평생 이용권 / OCR 충전권 100장)
          </div>
          {codeState === 'success' || codeState === 'credit_success' ? (
            <div style={{
              background: '#FFF7ED', border: '1.5px solid #EA580C', borderRadius: '10px',
              padding: '14px', textAlign: 'center', fontWeight: 800, color: '#C2410C',
            }}>
              {codeState === 'credit_success'
                ? '🎉 OCR 100장이 충전되었습니다!'
                : '🎉 평생 Pro가 활성화되었습니다! 감사합니다.'}
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  value={codeInput}
                  onChange={e => { setCodeInput(e.target.value); setCodeState(null); }}
                  onKeyDown={e => e.key === 'Enter' && codeState !== 'loading' && redeemCode()}
                  placeholder="SEMFIT-XXXX-XXXX"
                  style={{
                    flex: 1, padding: '11px 14px', borderRadius: '10px',
                    border: '1.5px solid #E4E4E7', fontSize: '0.95rem',
                    fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
                    outline: 'none',
                  }}
                />
                <button
                  onClick={redeemCode}
                  disabled={codeState === 'loading' || !codeInput.trim()}
                  style={{
                    padding: '11px 20px', borderRadius: '10px', border: 'none',
                    background: codeInput.trim() ? '#EA580C' : '#D6D3D1',
                    color: '#fff', fontWeight: 800, fontSize: '0.9rem',
                    cursor: codeInput.trim() ? 'pointer' : 'default', flexShrink: 0,
                  }}>
                  {codeState === 'loading' ? '확인 중...' : '등록'}
                </button>
              </div>
              {codeState && codeState.error && (
                <div style={{ marginTop: '10px', fontSize: '0.8rem', color: '#DC2626' }}>
                  {codeState.error}
                </div>
              )}
            </>
          )}
        </div>
      )}

      <div style={S.notice}>
        💡 평생 이용권은 래피드(Latpeed)에서 안전하게 결제됩니다. (카드 정보는 셈핏에 저장되지 않아요)<br />
        결제 확인 후 24시간 이내(영업일 기준) 래피드 메시지로 이용권 코드가 전달되며,
        위의 코드 입력란에 넣으면 평생 Pro가 바로 활성화됩니다.<br />
        1회 결제 상품으로 자동 갱신·추가 청구가 없습니다. 환불은 코드 등록 전 7일 이내 래피드를 통해 가능하며, 코드 등록(사용 개시) 후에는 불가합니다.<br />
        영수증 OCR은 공정 사용을 위해 총 1,000장(월 100장)이 기본 제공되며, 소진 시 충전권(100장 3,000원)으로 계속 이용할 수 있습니다.
        그 외 기능은 사용량 제한이 없습니다.
      </div>
    </div>
  );
}
